<template>
    <form class="container row align-items-center bg-white px-0 py-0 mx-auto shadow-sm mt-2" @submit.prevent="handleSubmit">
        <div class="col-lg-2 col-md-6 col-12 p-2 form-column xs-no-border">
            <div class="row px-3 pr-md-2">
                <div class="col-10 px-0">
                    <div class="text-sm text-muted px-2">
                        {{ $t('airport_transfers.components.search_form.from') }}:
                    </div>

                    <v-select
                        :options="startPlaces"
                        :loading="isStartPlacesLoading"
                        :placeholder="$t('airport_transfers.components.search_form.from')"
                        :class="{ 'is-filled': parameters.start_place }"
                        :title="parameters.start_place ? parameters.start_place.name : ''"
                        label="name"
                        v-model="parameters.start_place"
                        @search="handleSearchStartPlaces"
                    >
                        <template v-slot:no-options="{ search, searching }">
                            <p v-html="$t('components.taxi_search_form.search_no_results', { email: officeEmail })" />
                        </template>
                    </v-select>
                </div>

                <div class="col-2 px-0">
                    <button type="button" class="btn light p-1 mt-4" @click="swapLocations">
                        <font-awesome-icon icon="exchange-alt" />
                    </button>
                </div>
            </div>
        </div>

        <div class="col-lg-2 col-md-6 col-12 p-2 form-column sm-no-border">
            <div class="text-sm text-muted px-2">
                {{ $t('airport_transfers.components.search_form.to') }}:
            </div>

            <v-select
                :options="endPlaces"
                :loading="isEndPlacesLoading"
                :disabled="this.parameters.start_place === null"
                :class="{ 'is-filled': parameters.end_place }"
                :placeholder="$t('airport_transfers.components.search_form.to')"
                :title="parameters.end_place ? parameters.end_place.name : ''"
                label="name"
                v-model="parameters.end_place"
                @search="handleSearchEndPlaces"
            >
                <template v-slot:no-options="{ search, searching }">
                    <p v-html="$t('components.taxi_search_form.search_no_results', { email: officeEmail })" />
                </template>
            </v-select>
        </div>

        <div class="col-lg-2 col-md-6 col-12 p-2 form-column xs-no-border">
            <div class="row px-2">
                <div class="col-12">
                    <div class="text-sm text-muted">
                        {{ $t('departure_date') }}:
                    </div>
                </div>
            </div>
            <div class="row pr-3">
                <div class="col-12">
                    <date-picker
                        :placeholder="$t('airport_transfers.components.search_form.date_pick')"
                        style="border:none"
                        value-type="format"
                        :disabled-date="setDepartureDateRange"
                        v-model="parameters.departure_date"
                    />
                </div>
            </div>
        </div>

        <div class="col-lg-2 col-md-6 col-12 p-2 form-column sm-no-border" v-if="!parameters.one_way">
            <div class="row px-2">
                <div class="col-12">
                    <div class="text-sm text-muted">
                        {{ $t('return_date') }}:
                    </div>
                </div>
            </div>
            <div class="row pr-3" v-if="!parameters.one_way">
                <div class="col-12">
                    <date-picker
                        :placeholder="$t('airport_transfers.components.search_form.date_pick')"
                        style="border:none"
                        value-type="format"
                        :disabled="!parameters.departure_date"
                        :disabled-date="setReturnDateRange"
                        v-model="parameters.return_date"
                    />
                </div>
            </div>
        </div>

        <div
            class="col-md-12 col-12 text-center form-column sm-no-border cursor-pointer mt-0 mt-md-2 mt-lg-0"
            :class="{'col-lg-1' : !parameters.one_way, 'col-lg-2' : parameters.one_way}"
            @click="switchOneWay"
        >
            <div class="text-sm text-muted one-way-label">
                {{ $t('airport_transfers.components.search_form.one_way') }}
            </div>

            <input id="one-way" type="checkbox" name="one-way" v-model="parameters.one_way">
        </div>

        <div class="col-lg-2 col-sm-12 col-12 px-2 pb-2 form-column border-0">
            <div class="text-sm text-muted">
                {{ $t('passengers') }}:
            </div>

            <PassengersSelect :passengers.sync="passengers" :max-number="10" />
        </div>

        <div
            class="col-sm-12 col-12 px-0 h-100"
            :class="{'col-lg-1' : !parameters.one_way, 'col-lg-2' : parameters.one_way}"
        >
            <button type="submit" class="btn btn-primary w-100 h-100 py-lg-4 py-md-2">
                {{ $t('airport_transfers.components.search_form.submit') }}
            </button>
        </div>
    </form>
</template>

<script>
    import DatePicker from 'vue2-datepicker';
    import 'vue2-datepicker/index.css';
    import { debounce as _debounce } from 'lodash-es';
    import toastr from 'toastr';

    import i18n from '../../../i18n';
    import PassengersSelect from '@components/PassengersSelect';
    import { mapGetters } from 'vuex';
    import {trackGA} from '@utilities/tracking';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    export default {
        components: {
            DatePicker,
            PassengersSelect,
        },

        data() {
            return {
                startPlaces: [],
                endPlaces: [],

                isStartPlacesLoading: false,
                isEndPlacesLoading: false,

                cities: [
                    {id: 'Szczecin', name: 'Szczecin'},
                    {id: 'Gol', name: 'Szczecin Lotnisko Goleniów'},
                ],

                parameters: {
                    start_place: null,
                    end_place: null,
                    departure_date: null,
                    return_date: null,
                    one_way: false,
                },

                passengers: [
                    {
                        type: 'passenger',
                        label: i18n.tc('passengers'),
                        quantity: 1,
                        min: 1,
                    },
                ],
            };
        },
        computed: {
            ...mapGetters({
                officeEmail: 'constants/officeEmail',
                searchData: 'airportTransfersSearch/getSearchParameters',
            }),
        },

        created() {
            //reset if old format of search data is used
            if (this.searchData) {
                if(!this.cities.filter(c => c.id === this.searchData.start_place).length || !this.cities.filter(c => c.id === this.searchData.end_place).length){
                    this.$store.dispatch('airportTransfersSearch/reset');
                }
            }

            if (this.searchData) {
                this.parameters.start_place = {
                    id: this.searchData.start_place,
                    name : this.searchData.start_place_name,
                };
                this.parameters.end_place = {
                    id: this.searchData.end_place,
                    name: this.searchData.end_place_name,
                };
                this.parameters.departure_date = this.searchData.departure_date;
                this.parameters.return_date = this.searchData.return_date;
                this.parameters.one_way = !parseInt(this.searchData.with_return);
                this.passengers[0].quantity = this.searchData.passengers_number;
            }
        },

        methods: {
            handleSearchStartPlaces: _debounce(function (search, loading) {
                return this.fetchStartPlaces(search, loading);
            }, 500),

            handleSearchEndPlaces: _debounce(function (search, loading) {
                return this.fetchEndPlaces(search, loading);
            }, 500),

            fetchStartPlaces(search, loading) {
                if (search.length <= 2) {
                    return;
                }
                this.isStartPlacesLoading = true;

                //search in this.cities
                this.startPlaces = this.cities.filter(city => city.name.toLowerCase().includes(search.toLowerCase()));
                this.isStartPlacesLoading = false;
            },

            fetchEndPlaces(search, loading) {
                if (search.length <= 2) {
                    return;
                }
                this.isEndPlacesLoading = true;

                //search in this.cities excluding start_place id
                this.endPlaces = this.cities.filter(city => city.name.toLowerCase().includes(search.toLowerCase()) && city.id !== this.parameters.start_place.id);
                this.isEndPlacesLoading = false;
            },

            handleSubmit() {
                if (!this.parameters.start_place) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('from')}));

                    return;
                }

                if (!this.parameters.end_place) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('to')}));

                    return;
                }

                if (!this.parameters.departure_date) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('departure_date')}));

                    return;
                }

                if (!this.parameters.one_way && !this.parameters.return_date) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('return_date')}));

                    return;
                }

                if (this.passengers[0].quantity === 0) {
                    toastr.error(this.$t('taxi.validation.at_least_one_passenger_required'));

                    return;
                }

                //tracking
                if(this.$attrs['tracking-name']){
                    trackGA('begin_checkout', {
                        // 'items': [
                        //     {
                        //         id: window.location.pathname,
                        //         name: this.$attrs['tracking-name'],
                        //         quantity: 1,
                        //     },
                        // ],
                    });

                    trackGA('set_checkout_option', {
                        'checkout_step': 1,
                        'checkout_option': 'search from',
                        'value': this.parameters.start_place.name,
                    });

                    trackGA('set_checkout_option', {
                        'checkout_step': 1,
                        'checkout_option': 'search to',
                        'value': this.parameters.end_place.name,
                    });

                    trackGA('set_checkout_option', {
                        'checkout_step': 1,
                        'checkout_option': 'search departure date',
                        'value': this.parameters.departure_date,
                    });

                    trackGA('set_checkout_option', {
                        'checkout_step': 1,
                        'checkout_option': 'search return date',
                        'value': this.parameters.return_date,
                    });

                    trackGA('set_checkout_option', {
                        'checkout_step': 1,
                        'checkout_option': 'search passengers',
                        'value': this.passengers[0].quantity,
                    });

                    trackGA('set_checkout_option', {
                        'checkout_step': 1,
                        'checkout_option': 'search one way only',
                        'value': this.parameters.one_way ? 0 : 1,
                    });
                }

                this.$emit('search', {
                    start_place: this.parameters.start_place.id,
                    start_place_name: this.parameters.start_place.name,
                    end_place: this.parameters.end_place.id,
                    end_place_name: this.parameters.end_place.name,
                    departure_date: this.parameters.departure_date,
                    return_date: this.parameters.return_date,
                    passengers_number: this.passengers[0].quantity,
                    with_return: this.parameters.one_way ? 0 : 1,
                });
            },

            swapLocations() {
                const newEndPlace = this.parameters.start_place;

                this.parameters.start_place = this.parameters.end_place;
                this.parameters.end_place = newEndPlace;
            },

            setDepartureDateRange(date) {
                let endDate = new Date();
                endDate.setDate(today.getDate() + 512);
                return date < today || date > endDate;
            },
            setReturnDateRange(date) {
                let endDate = new Date();
                endDate.setDate(today.getDate() + 512);

                if (this.parameters.departure_date){
                    let departure_date = new Date(this.parameters.departure_date);
                    return date < departure_date || date > endDate;
                }
                return date < today || date > endDate;
            },

            switchOneWay() {
                this.parameters.one_way = !this.parameters.one_way;
                this.parameters.return_date = null;
            },
        },
    };
</script>

<style scoped>
    .form-column:not(:last-child) {
        border-right: 1px solid lightgray;
    }

    .v-select >>> .vs__selected {
        text-overflow: ellipsis;
        width: 73%;
        display: inline-block;
        white-space: nowrap;
        overflow: hidden;
    }

    @media only screen and (min-width: 992px) and (max-width: 1200px) {
        .v-select >>> .vs__selected {
            width: 60%;
        }
    }

    @media only screen and (max-width: 991px) {
        .v-select >>> .vs__selected {
            width: 100%;
        }
    }

    .v-select >>> .vs__no-options {
        padding: 0.2rem 0.4rem;
    }

    .v-select.vs--open >>> .vs__selected {
        display: none;
    }

    .v-select >>> .vs__clear {
        display: none !important;
    }
    .v-select >>> .vs__dropdown-menu {
        width: fit-content;
        border-left: none;
    }
    .v-select >>> .vs__dropdown-toggle {
        border: none;
    }
    .v-select >>> .vs__dropdown-toggle .vs__selected-options .vs__search::placeholder {
        color:#5f6a70;
    }
    .v-select >>> .vs__open-indicator {
        display: none;
    }
    .v-select.is-filled:not(.vs--open) >>> .vs__search {
        height: 0;
        padding: 0;
    }
    .mx-datepicker >>> .mx-input-wrapper .mx-input {
        border:none;
        padding-right: 20px;
        box-shadow: none;
        pointer-events: none;
    }
    .mx-datepicker >>> .mx-input-wrapper .mx-input::placeholder {
        font-size: 16px;
        color: #5f6a70;
    }
    .mx-datepicker >>> .mx-input-wrapper .mx-icon-calendar {
        display: none;
    }

    .one-way-label {
        line-height: 1rem;
    }
</style>
